/**
 * Static prop → 3D model registry (#816) — the props counterpart of
 * `models.ts`'s champion→GLB map, and the SINGLE SWAP POINT for 3D terrain
 * art: pointing a prop kind at a different GLB (or adding a second prop kind,
 * e.g. #488's walls / an ice wall) is a DATA edit here, never a change to the
 * render layer.
 *
 * A prop is static map/terrain geometry: no skeleton, no clips, no facing, and
 * no per-frame reconciliation (`threeLayer.setProps` replaces the whole set at
 * arena load, unlike `syncCharacters`, which runs every frame).
 *
 * SIZING IS DATA, NOT CODE. Each entry carries its height rule as a multiple of
 * its footprint DIAMETER (`heightDiameters`), which the runtime applies to the
 * COLLISION RADIUS FROM MAP DATA (`propHeightUnits`) — never a baked per-model
 * number and never the GLB's own export scale. The pillar's rule is `1.6`
 * (height = 1.6 × diameter = 3.2 × collision radius, #829; it was `2` from #816
 * until the `bare` pillars were widened).
 *
 * WHY 1.6 (#829, "25% wider, NOT taller"): height is DERIVED from the collision
 * radius, so widening `bare`'s pillars 100 → 125 would have made them 25%
 * TALLER as a side effect (400 → 500) — exactly what the request excluded.
 * `1.6 = 400 / (2 × 125)` compensates, holding `bare`'s rendered height at the
 * 400 units it has always had. `heightDiameters` is per-prop-KIND, not per-map,
 * so this also reshapes `nagrand`'s r=100 pillars: 400 → 320, i.e. 20%
 * shorter. That side effect was raised and ACCEPTED by the owner (#829 Q1
 * option A) rather than escaping the "sizing is derived from map data"
 * invariant with a per-map override — we retune the derivation constant, we do
 * not add a second source of truth. Both heights are pinned by
 * `pillarSizing.test.ts`.
 *
 * PRESENTATION ONLY — nothing here touches the sim or any hash.
 */

/**
 * The static prop kinds that can render in 3D. A closed union so a new prop is
 * an explicit, reviewable data addition (and `PROP_MODELS` below is
 * exhaustively keyed by it).
 */
export type PropKind = "pillar";

/** A static prop's 3D model binding + its map-data-derived sizing rule. */
export interface Prop3DModel {
  readonly propKind: PropKind;
  /** Art slug for the committed GLB (folder/file stem). */
  readonly modelSlug: string;
  /** Path to the GLB under the client's static asset root (`public/`). */
  readonly assetPath: string;
  /**
   * Height as a multiple of the prop's footprint DIAMETER. Applied to the
   * collision radius from map data by {@link propHeightUnits}; the pillar's
   * `1.6` means "1.6× as tall as it is wide" = 3.2 × collision radius (#829).
   */
  readonly heightDiameters: number;
}

/** The pillar column (#816) — collision-registered against `MAP_SCHEMA` pillars.
 *  `heightDiameters` retuned 2 → 1.6 by #829 so widening `bare`'s pillars did
 *  not also make them taller; see this module's header for the full rationale. */
const PILLAR_PROP: Prop3DModel = {
  propKind: "pillar",
  modelSlug: "pillar",
  assetPath: "models/pillar.glb",
  heightDiameters: 1.6,
};

/** Prop kind → 3D model binding. Pillar-only for #816. */
const PROP_MODELS: Readonly<Record<PropKind, Prop3DModel>> = {
  pillar: PILLAR_PROP,
};

/** Every prop kind with a 3D binding, in declaration order. */
export const PROP_KINDS: readonly PropKind[] = Object.keys(PROP_MODELS) as PropKind[];

/** The 3D model binding for a prop kind. Total — every kind has one entry. */
export function prop3DFor(kind: PropKind): Prop3DModel {
  return PROP_MODELS[kind];
}

/**
 * Height in SIM UNITS for a prop sitting on a collision circle of
 * `radiusUnits`: `2 × radiusUnits × heightDiameters`. Derived from map data on
 * every call — the load-bearing "never a baked per-model height" rule (#816).
 * For the pillar (`heightDiameters` 1.6, #829) that is 400 units on `bare`
 * (r=125) and 320 on `nagrand` (r=100).
 *
 * A degenerate (non-positive / non-finite) radius yields `0`, which
 * `instantiateProp`'s scale guard then treats as "leave the axis unscaled"
 * rather than collapsing or inverting the model.
 */
export function propHeightUnits(model: Prop3DModel, radiusUnits: number): number {
  if (!(radiusUnits > 0)) return 0;
  return 2 * radiusUnits * model.heightDiameters;
}

/**
 * Resolve a prop model's runtime URL, honouring the Vite base path so it loads
 * under a sub-path deploy. Mirrors `model3DUrl`; kept separate from the pure
 * registry so the registry stays trivially testable.
 */
export function prop3DUrl(model: Prop3DModel): string {
  return `${import.meta.env.BASE_URL}${model.assetPath}`;
}
